import { useMemo } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { Skeleton } from "@volca/ui/components/ui/skeleton";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { cn } from "@volca/ui/lib/utils";
import { createDefaults } from "@/core/utils/loading";
import { getSignupRank } from "@/referrals/utils";
import { useGetUsersWithStats } from "@/user/query";
import { isAdmin } from "@/user/utils";
import { SignupLeaderboardItem } from "./signup-leaderboard-item";

const LOADING_ROWS = createDefaults("leaderboard-loading", 5);

interface SignupLeaderboardProps {
  className?: string;
  businessId: string;
}

export const SignupLeaderboard = ({
  className,
  businessId,
}: SignupLeaderboardProps) => {
  const {
    data: users = [],
    isLoading,
    isError,
    refetch,
  } = useGetUsersWithStats(businessId, "last_thirty_days");

  const rankedUsers = useMemo(() => {
    const sorted = users
      .filter((user) => !isAdmin(user.role))
      .sort(
        (a, b) => (b.stats?.signup_count ?? 0) - (a.stats?.signup_count ?? 0),
      );
    return sorted.map((user) => ({ user, rank: getSignupRank(sorted, user) }));
  }, [users]);

  const hasRanks = rankedUsers.some(({ rank }) => rank !== null);

  return (
    <Stack align="start" gap={2} className={cn("w-full pt-4", className)}>
      <Text variant="label" className="px-4">
        Sign-ups in the last 30 days
      </Text>
      <div className="min-h-0 w-full flex-1 overflow-y-auto">
        {isLoading &&
          LOADING_ROWS.map((row) => (
            <Stack
              key={row.id}
              direction="row"
              align="center"
              gap={2}
              className="h-12 px-4"
            >
              <Skeleton className="size-6 rounded-full" />
              <Skeleton className="h-4 flex-1" />
            </Stack>
          ))}
        {!isLoading && isError && (
          <Stack align="start" gap={2} className="px-4 pb-4">
            <Text tone="muted">Couldn&apos;t load the leaderboard.</Text>
            <Button variant="outline" size="sm" onClick={() => void refetch()}>
              Try again
            </Button>
          </Stack>
        )}
        {!isLoading && !isError && rankedUsers.length === 0 && (
          <Text tone="muted" className="px-4 pb-4">
            No team members yet.
          </Text>
        )}
        {!isLoading &&
          !isError &&
          rankedUsers.map(({ user, rank }, index) => (
            <SignupLeaderboardItem
              key={user.id}
              user={user}
              rank={rank}
              showRank={hasRanks}
              businessId={businessId}
              isLast={index === rankedUsers.length - 1}
            />
          ))}
      </div>
    </Stack>
  );
};
